import React, { useState } from "react";
import { Trash2, AlertTriangle, Loader2, ChevronRight } from "lucide-react";
import { userService } from "../../../services/UserService";
import { useToastContext } from "../../../context/ToastContext";

const DeleteAccount: React.FC = () => {
  const { showToast } = useToastContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  const handleRequestDeletion = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await userService.requestAccountDeletion({
        reason: reason || undefined,
        additionalInfo: additionalInfo || undefined,
      });
      setRequestSubmitted(true);
      setShowRequestForm(false);
      showToast("success", "Deletion request submitted. Support will contact you shortly.");
    } catch (err: any) {
      const status = err?.response?.status;
      let errorMessage = "Failed to submit deletion request. Please try again.";

      if (status === 429) {
        errorMessage = "Too many requests. Please try again in an hour.";
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      showToast("error", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-background-paper dark:bg-background-paperDark rounded-lg border border-red-500/30 dark:border-red-500/20 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-1.5 rounded bg-red-500/10 dark:bg-red-500/20">
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
              Delete Account
            </h3>
            <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-0.5">
              Request account deletion (support will contact you)
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-4">
          {requestSubmitted && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-secondary/10 dark:bg-secondary/20 text-secondary">
              <AlertTriangle className="w-3 h-3" />
              <span className="text-xs font-medium">Request Submitted</span>
            </div>
          )}

          {error && !showRequestForm && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-red-500/10 dark:bg-red-500/20 text-red-500">
              <AlertTriangle className="w-3 h-3" />
              <span className="text-xs">{error}</span>
            </div>
          )}

          {!requestSubmitted && (
            <button
              onClick={() => {
                setShowRequestForm(true);
                setError(null);
              }}
              className="px-3 py-1.5 text-xs font-medium bg-background-panel dark:bg-background-panelDark hover:bg-background-default dark:hover:bg-white/5 border border-divider dark:border-divider-dark rounded text-text-primary dark:text-text-primaryDark transition-colors flex items-center gap-1.5"
            >
              <span>Request</span>
              <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {showRequestForm && (
        <div className="mt-4 pt-4 border-t border-divider dark:border-divider-dark">
          <div className="mb-3">
            <p className="text-xs text-text-secondary dark:text-text-secondaryDark mb-3">
              This will send a deletion request to our support team. Your account will not be deleted immediately.
            </p>
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-medium text-text-primary dark:text-text-primaryDark mb-1">
                  Reason (optional)
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-divider dark:border-divider-dark rounded bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark focus:outline-none focus:ring-1 focus:ring-secondary/20 focus:border-secondary"
                >
                  <option value="">Select a reason...</option>
                  <option value="No longer needed">No longer needed</option>
                  <option value="Found alternative solution">Found alternative solution</option>
                  <option value="Privacy concerns">Privacy concerns</option>
                  <option value="Too expensive">Too expensive</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-primary dark:text-text-primaryDark mb-1">
                  Additional Information (optional)
                </label>
                <textarea
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  rows={2}
                  placeholder="Tell us more..."
                  className="w-full px-2 py-1.5 text-xs border border-divider dark:border-divider-dark rounded bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-1 focus:ring-secondary/20 focus:border-secondary resize-none"
                />
              </div>
            </div>
          </div>
          {error && (
            <div className="mb-3 text-xs text-red-500">{error}</div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setShowRequestForm(false);
                setError(null);
                setReason("");
                setAdditionalInfo("");
              }}
              disabled={isLoading}
              className="px-3 py-1.5 text-xs font-medium bg-background-panel dark:bg-background-panelDark hover:bg-background-default dark:hover:bg-white/5 border border-divider dark:border-divider-dark rounded text-text-primary dark:text-text-primaryDark transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleRequestDeletion}
              disabled={isLoading}
              className="px-3 py-1.5 text-xs font-medium bg-red-500 hover:bg-red-600 text-white rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <span>Submit Request</span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteAccount;
